import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { z } from 'zod';
import { toast } from 'sonner';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { AccountService } from '@/features/auth/services/AccountService';

const changePasswordSchema = z.object({
  oldPassword: requiredInput(z.string().min(1)),
  newPassword: requiredInput(z.string().min(8)),
  confirmNewPassword: requiredInput(z.string().min(1)),
});

export function ChangePasswordTab() {
  const form = useAppForm(
    appFormOptions({
      schema: changePasswordSchema,
      defaultValues: {
        oldPassword: null,
        newPassword: null,
        confirmNewPassword: null,
      },
      onSubmit: async ({ value }) => {
        await AccountService.changePassword({
          oldPassword: value.oldPassword,
          newPassword: value.newPassword,
        });
        toast.success(t`Password changed successfully`);
      },
    })
  );

  return (
    <div className="max-w-xl">
      <div className="rounded-2xl border border-border bg-card p-6">
        <form.AppForm>
          <form.FormElement>
            <div className="space-y-4">
              <FormErrors />

              <form.AppField name="oldPassword">
                {(field) => (
                  <field.TextField label={<Trans>Current password</Trans>} type="password" />
                )}
              </form.AppField>

              <form.AppField name="newPassword">
                {(field) => <field.TextField label={<Trans>New password</Trans>} type="password" />}
              </form.AppField>

              <form.AppField
                name="confirmNewPassword"
                validators={{
                  onChange: ({ value, fieldApi }) => {
                    const newPassword = fieldApi.form.getFieldValue('newPassword');
                    if (value !== null && newPassword !== null && value !== newPassword) {
                      return t`Passwords do not match`;
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <field.TextField label={<Trans>Confirm new password</Trans>} type="password" />
                )}
              </form.AppField>

              <FormSubmit>
                <Trans>Change password</Trans>
              </FormSubmit>
            </div>
          </form.FormElement>
        </form.AppForm>
      </div>
    </div>
  );
}
